import React, { FC } from "react";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { ModalLayout } from "react-migration/components/ModalLayout";

type ModalV3Props = {
  onClose?: () => void;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  children?: React.ReactNode;
  withCloseButton?: boolean;
  size?: "small" | "large";
  pagination?: React.ReactNode;
  fullWidth?: boolean;
  fullHeight?: boolean;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
};

export const ModalV3: FC<ModalV3Props> = ({
  onClose,
  title,
  description,
  action,
  children,
  withCloseButton = true,
  size = "small",
  pagination,
  fullWidth = false,
  fullHeight = false,
  tabIndex,
  onKeyDown,
}: ModalV3Props) => {
  const { t } = useTranslation();

  return (
    <ModalLayout
      onClose={onClose}
      size={size}
      fullWidth={fullWidth}
      fullHeight={fullHeight}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      portalParent={document.body}
    >
      <div className="flex flex-row items-center justify-between w-full lt-modal-header flex-nowrap">
        {<h2 className="text-lg font-semibold text-blue-grey-950">{title}</h2>}
        <Button
          className="modal-close"
          onClick={onClose}
          aria-label={t("modal.close_aria_label")}
          size="sm"
          variant="ghost"
        >
          <Icon icon="icon-lt-close-line" size="lg" />
        </Button>
      </div>
      {(description || pagination) && (
        <div className="flex flex-row items-center justify-between w-full gap-2 flex-nowrap">
          {description && <p className="m-0 text-sm">{description}</p>}
          {pagination && <div className="flex flex-row items-center justify-end">{pagination}</div>}
        </div>
      )}
      {children && (
        <div className="flex flex-col w-full h-full p-0 m-0 overflow-y-auto text-sm lt-modal-content">{children}</div>
      )}
      {(withCloseButton || action) && (
        <div className="flex flex-row items-center justify-end w-full gap-4">
          {withCloseButton && (
            <Button
              className="self-start"
              variant={action ? "tertiary" : "primary"}
              size="md"
              onClick={onClose}
              aria-label={t("modal.close_aria_label")}
            >
              {t("general.close")}
            </Button>
          )}

          {action && (
            <Button className="self-end" variant="primary" size="md" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      )}
    </ModalLayout>
  );
};

ModalV3.displayName = "ModalV3";
